import { ErrorBoundary } from '@monorepo/shared/componentsV2/ErrorBoundary';
import {
  InfoCard,
  InfoCardVariant,
} from '@monorepo/shared/componentsV2/InfoCard';
import { InputLabel } from '@monorepo/shared/componentsV2/Input';
import { Loading } from '@monorepo/shared/componentsV2/Loading';
import { useDatasets } from '@monorepo/shared/hooks/workflows/useDatasets';
import { isRequired } from '@monorepo/shared/utils/validators';
import WarningIcon from '@svg/warning.svg';
import { DatasetType } from 'mapistry-shared';
import React from 'react';
import { useField } from 'react-final-form';
import { useParams } from 'react-router-dom-v5-compat';
import styled from 'styled-components';
import { DatasetPicker } from '../../shared/DatasetPicker/DatasetPicker';
import { EmptyDatasetList } from '../../shared/DatasetPicker/EmptyDatasetList';
import {
  InfoContainer,
  StyledHelperText,
} from '../../shared/DatasetPicker/styled';
import { DatasetErrorFallback } from './DatasetErrorFallback';
import { SelectedDataset } from './SelectedDataset';

const StyledInfoCard = styled(InfoCard)`
  max-width: 32rem;

  .MuiCardContent-root {
    gap: 0.25rem;
    padding: 0.75rem;
  }
`;

const WarningText = styled.div`
  line-height: 1.5;
`;

function DatasetsDisplay() {
  const { organizationId, workflowId } = useParams();
  const workflowExists = !!workflowId;

  const { datasets, isFetching } = useDatasets({
    config: { enabled: !workflowExists },
    organizationId,
  });
  const fieldName = 'inputDatasetId';
  const { input } = useField<string>(fieldName);

  if (workflowExists) {
    return <SelectedDataset />;
  }

  if (isFetching) {
    return (
      <InfoContainer>
        <Loading size="medium" />
      </InfoContainer>
    );
  }

  if (!datasets || !datasets.length) {
    return <EmptyDatasetList includeWorkflows={false} />;
  }

  const selectedDataset = datasets.find((d) => d.id === input.value);

  return (
    <>
      <DatasetPicker
        datasets={datasets}
        fieldName={fieldName}
        isLabelHidden
        label="Data source"
      />
      {selectedDataset?.type === DatasetType.RESOURCE_TYPE && (
        <StyledInfoCard
          variant={InfoCardVariant.WARNING}
          icon={<WarningIcon />}
        >
          <WarningText>
            You have chosen a Resource as your starting Data source. You will
            not be able to connect this Workflow with a Log or another Workflow
            that references a Log. If you&apos;d like to connect a Resource with
            a Log or another Workflow, you must choose the Log or Workflow as
            the starting Datasource.
          </WarningText>
        </StyledInfoCard>
      )}
    </>
  );
}

export const DatasetSection = () => {
  const { meta } = useField<string>('inputDatasetId', { validate: isRequired });
  const hasError = (!!meta.error && (meta.touched || meta.modified)) || false;

  return (
    <>
      <InputLabel required error={hasError}>
        Data source
      </InputLabel>
      <StyledHelperText>
        Choose the data source that you&apos;d like to build your workflow from.
        You can join additional data sources within the workflow in the next
        steps. This cannot be changed after the Workflow is created.
      </StyledHelperText>
      <ErrorBoundary Fallback={DatasetErrorFallback}>
        <DatasetsDisplay />
      </ErrorBoundary>
    </>
  );
};
